import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Mail, 
  Inbox, 
  PenSquare, 
  Send, 
  Loader2, 
  RefreshCw, 
  Search,
  ChevronRight,
  Clock,
  User,
  ExternalLink,
  MessageSquare,
  CheckCircle,
  Eye,
  MousePointer,
  Paperclip,
  FileText,
  X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ===================== INBOX COMPONENTS =====================

function InboxThread({ thread, onClick }) {
  const fromName = thread.from?.split('<')[0]?.trim() || thread.from || 'Unknown';
  const dateStr = thread.date ? new Date(thread.date).toLocaleDateString() : '';
  const timeStr = thread.date ? new Date(thread.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  
  return (
    <div 
      className="p-4 border-b border-[#E5E7EB] cursor-pointer hover:bg-gray-50 transition-colors"
      onClick={onClick}
      data-testid={`inbox-thread-${thread.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          thread.is_unread ? 'bg-[#C9A227] text-white' : 'bg-gray-200 text-gray-600'
        }`}>
          <User className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {thread.is_unread && <span className="w-2 h-2 bg-[#C9A227] rounded-full flex-shrink-0" />}
              <p className={`text-sm truncate ${thread.is_unread ? 'font-semibold text-[#1A1A1A]' : 'font-medium text-[#374151]'}`}>
                {fromName}
              </p>
            </div>
            <span className="text-xs text-[#9CA3AF] whitespace-nowrap">{dateStr}</span>
          </div>
          <p className={`text-sm truncate mt-0.5 ${thread.is_unread ? 'font-medium text-[#1A1A1A]' : 'text-[#6B7280]'}`}>
            {thread.subject || '(No subject)'}
          </p>
          <p className="text-xs text-[#9CA3AF] truncate mt-1">{thread.snippet}</p>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-xs rounded-sm">
              <MessageSquare className="w-3 h-3 mr-1" />{thread.message_count}
            </Badge>
            <span className="text-xs text-[#9CA3AF]">{timeStr}</span>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-[#9CA3AF] flex-shrink-0 mt-1" />
      </div>
    </div>
  );
}

function ThreadView({ thread, token, onBack, onRefresh }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(function() {
    if (thread) fetchThread();
  }, [thread?.id]);

  async function fetchThread() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/gmail/thread/${thread.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Error fetching thread:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSendReply() {
    if (!replyBody.trim()) return;
    setSending(true);
    try {
      const lastMessage = messages[messages.length - 1];
      const toEmail = lastMessage?.from?.includes('<') 
        ? lastMessage.from.match(/<(.+)>/)?.[1] || lastMessage.from 
        : lastMessage.from;
      const res = await fetch(`${API}/api/gmail/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: toEmail,
          subject: `Re: ${thread.subject || ''}`,
          body: replyBody,
          thread_id: thread.id
        })
      });
      if (res.ok) {
        setReplyBody('');
        setReplyOpen(false);
        fetchThread();
        onRefresh();
      }
    } catch (err) {
      console.error('Error sending reply:', err);
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-[#E5E7EB] bg-white sticky top-0">
        <Button variant="ghost" size="sm" onClick={onBack} className="mb-2">← Back to Inbox</Button>
        <h2 className="text-lg font-medium text-[#1A1A1A]">{thread.subject || '(No subject)'}</h2>
        <p className="text-sm text-[#6B7280]">{messages.length} messages</p>
      </div>
      <div className="flex-1 overflow-y-auto">
        {messages.map(function(msg, idx) {
          return (
            <div key={msg.id} className={`p-6 border-b border-[#E5E7EB] ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-medium text-[#1A1A1A]">{msg.from}</p>
                  <p className="text-xs text-[#6B7280]">To: {msg.to}</p>
                </div>
                <p className="text-xs text-[#9CA3AF]">{msg.date}</p>
              </div>
              <div className="text-sm text-[#374151] prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: msg.body || msg.snippet }} />
            </div>
          );
        })}
      </div>
      <div className="p-4 border-t border-[#E5E7EB] bg-white sticky bottom-0">
        {replyOpen ? (
          <div className="space-y-3">
            <Textarea placeholder="Write your reply..." value={replyBody} onChange={function(e) { setReplyBody(e.target.value); }} rows={4} className="resize-none" />
            <div className="flex items-center gap-2">
              <Button onClick={handleSendReply} disabled={sending || !replyBody.trim()} className="bg-[#C9A227] hover:bg-[#B8922A]">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Send Reply
              </Button>
              <Button variant="outline" onClick={function() { setReplyOpen(false); }}>Cancel</Button>
            </div>
          </div>
        ) : (
          <Button onClick={function() { setReplyOpen(true); }} className="w-full bg-[#1A1A1A] hover:bg-[#333]">
            <Send className="w-4 h-4 mr-2" />Reply to this conversation
          </Button>
        )}
      </div>
    </div>
  );
}

function InboxView({ token, unreadCount, onUnreadChange }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedThread, setSelectedThread] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(function() { fetchThreads(); }, []);

  async function fetchThreads() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/gmail/threads?max_results=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      const threadList = data.threads || [];
      setThreads(threadList);
      const unread = threadList.filter(function(t) { return t.is_unread; }).length;
      onUnreadChange(unread);
    } catch (err) {
      console.error('Error fetching threads:', err);
    } finally {
      setLoading(false);
    }
  }

  const filteredThreads = threads.filter(function(thread) {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (thread.subject || '').toLowerCase().includes(query) ||
           (thread.from || '').toLowerCase().includes(query) ||
           (thread.snippet || '').toLowerCase().includes(query);
  });

  if (selectedThread) {
    return <ThreadView thread={selectedThread} token={token} onBack={function() { setSelectedThread(null); }} onRefresh={fetchThreads} />;
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-[#E5E7EB] bg-white">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-[#1A1A1A] flex items-center gap-2">
            <Inbox className="w-5 h-5 text-[#C9A227]" />Inbox
            {unreadCount > 0 && <Badge className="bg-[#C9A227] text-white rounded-full px-2">{unreadCount}</Badge>}
          </h2>
          <Button variant="outline" size="sm" onClick={fetchThreads} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
          <Input placeholder="Search emails..." value={searchQuery} onChange={function(e) { setSearchQuery(e.target.value); }} className="pl-10" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#C9A227]" /></div>
        ) : filteredThreads.length === 0 ? (
          <div className="text-center py-20">
            <Mail className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" />
            <p className="text-[#6B7280]">{searchQuery ? 'No emails match your search' : 'No emails yet'}</p>
          </div>
        ) : (
          filteredThreads.map(function(thread) {
            return <InboxThread key={thread.id} thread={thread} onClick={function() { setSelectedThread(thread); }} />;
          })
        )}
      </div>
    </div>
  );
}

// ===================== SENT EMAILS COMPONENT =====================

function SentEmailItem({ email }) {
  const sentDate = email.sent_at ? new Date(email.sent_at) : null;
  const dateStr = sentDate ? sentDate.toLocaleDateString() : '';
  const timeStr = sentDate ? sentDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  
  return (
    <div className="p-4 border-b border-[#E5E7EB] hover:bg-gray-50 transition-colors">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-[#C9A227] text-white">
          <Send className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-[#1A1A1A] truncate">
              To: {email.recipient_name || email.recipient_email}
            </p>
            <span className="text-xs text-[#9CA3AF] whitespace-nowrap">{dateStr}</span>
          </div>
          <p className="text-sm text-[#374151] truncate mt-0.5">{email.subject}</p>
          <p className="text-xs text-[#9CA3AF] truncate mt-1">Template: {email.design_name || 'Custom'}</p>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-xs text-[#9CA3AF]">{timeStr}</span>
            {email.opened_at && (
              <Badge variant="outline" className="text-xs rounded-sm text-green-600 border-green-200">
                <Eye className="w-3 h-3 mr-1" />Opened
              </Badge>
            )}
            {email.clicked_at && (
              <Badge variant="outline" className="text-xs rounded-sm text-blue-600 border-blue-200">
                <MousePointer className="w-3 h-3 mr-1" />Clicked
              </Badge>
            )}
            {!email.opened_at && !email.clicked_at && (
              <Badge variant="outline" className="text-xs rounded-sm text-gray-500">
                <CheckCircle className="w-3 h-3 mr-1" />Delivered
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SentView({ token }) {
  const [sentEmails, setSentEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(function() { fetchSentEmails(); }, []);

  async function fetchSentEmails() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/email-sends`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSentEmails(data || []);
    } catch (err) {
      console.error('Error fetching sent emails:', err);
    } finally {
      setLoading(false);
    }
  }

  const filteredEmails = sentEmails.filter(function(email) {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (email.subject || '').toLowerCase().includes(query) ||
           (email.recipient_email || '').toLowerCase().includes(query) ||
           (email.recipient_name || '').toLowerCase().includes(query);
  });

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-[#E5E7EB] bg-white">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-[#1A1A1A] flex items-center gap-2">
            <Send className="w-5 h-5 text-[#C9A227]" />Sent
            <Badge variant="outline" className="rounded-full">{sentEmails.length}</Badge>
          </h2>
          <Button variant="outline" size="sm" onClick={fetchSentEmails} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
          <Input placeholder="Search sent emails..." value={searchQuery} onChange={function(e) { setSearchQuery(e.target.value); }} className="pl-10" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#C9A227]" /></div>
        ) : filteredEmails.length === 0 ? (
          <div className="text-center py-20">
            <Send className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" />
            <p className="text-[#6B7280]">{searchQuery ? 'No sent emails match your search' : 'No sent emails yet'}</p>
            <p className="text-xs text-[#9CA3AF] mt-2">Emails sent from the Email Designer will appear here</p>
          </div>
        ) : (
          filteredEmails.map(function(email) {
            return <SentEmailItem key={email.id || email.tracking_id} email={email} />;
          })
        )}
      </div>
    </div>
  );
}

// ===================== COMPOSE VIEW COMPONENT =====================

function ComposeView({ token, initialTo = '', initialName = '', opportunityId = '' }) {
  const [to, setTo] = useState(initialTo);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setTo(initialTo);
  }, [initialTo]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    try {
      for (const file of files) {
        // Validate file type
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
          toast.error(`${file.name}: Only PDF and image files are allowed`);
          continue;
        }
        
        // Validate size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: File must be under 10MB`);
          continue;
        }

        // Upload to server
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch(`${API}/api/media/upload-attachment`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (res.ok) {
          const data = await res.json();
          setAttachments(prev => [...prev, {
            id: data.id,
            name: file.name,
            size: file.size,
            type: file.type,
            url: data.url
          }]);
          toast.success(`${file.name} attached`);
        } else {
          const error = await res.json();
          toast.error(error.detail || `Failed to upload ${file.name}`);
        }
      }
    } catch (err) {
      toast.error('Error uploading files');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  async function handleSendEmail() {
    if (!to.trim() || !subject.trim() || !body.trim()) {
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${API}/api/gmail/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to: to,
          subject: subject,
          body: body,
          opportunity_id: opportunityId || undefined,
          attachments: attachments.map(a => ({ id: a.id, name: a.name, url: a.url, type: a.type }))
        })
      });

      if (res.ok) {
        setSubject('');
        setBody('');
        setAttachments([]);
        toast.success('Email sent successfully!');
      } else {
        const errorData = await res.json();
        const errorMessage = typeof errorData === 'string' 
          ? errorData 
          : errorData.detail || errorData.message || errorData.error || 'Failed to send email';
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Error sending email:', err);
      const errorMessage = err?.message || 'Error sending email';
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="p-6 space-y-4">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1">To</label>
          <Input
            placeholder="recipient@email.com"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="border-[#E5E7EB]"
            data-testid="compose-to-input"
          />
          {initialName && (
            <p className="text-xs text-[#6B7280] mt-1">Sending to: {initialName}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1">Subject</label>
          <Input
            placeholder="Email subject..."
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="border-[#E5E7EB]"
            data-testid="compose-subject-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1">Message</label>
          <Textarea
            placeholder="Write your message..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            className="resize-none border-[#E5E7EB]"
            data-testid="compose-body-input"
          />
        </div>
        
        {/* Attachments Section */}
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-2">Attachments</label>
          
          {/* Attachment List */}
          {attachments.length > 0 && (
            <div className="mb-3 space-y-2">
              {attachments.map(att => (
                <div key={att.id} className="flex items-center justify-between p-2 bg-[#F3F4F6] rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#C9A227]" />
                    <span className="text-sm text-[#374151]">{att.name}</span>
                    <span className="text-xs text-[#6B7280]">
                      ({(att.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeAttachment(att.id)}
                    className="h-6 w-6 p-0 text-[#6B7280] hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          {/* Upload Button */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".pdf,.jpg,.jpeg,.png,.gif"
            multiple
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="border-dashed"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Paperclip className="w-4 h-4 mr-2" />
            )}
            {uploading ? 'Uploading...' : 'Attach PDF or Image'}
          </Button>
          <p className="text-xs text-[#6B7280] mt-1">PDF, JPG, PNG, GIF up to 10MB each</p>
        </div>
        
        <div className="flex gap-2 justify-end">
          <Button
            onClick={handleSendEmail}
            disabled={sending || !to.trim() || !subject.trim() || !body.trim()}
            className="bg-[#C9A227] hover:bg-[#B8922A]"
            data-testid="compose-send-btn"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Send Email
          </Button>
        </div>
      </div>
    </div>
  );
}

// ===================== MAIN COMPONENT =====================

export default function Emails() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [gmailStatus, setGmailStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Check for compose params from opportunity page
  const toEmail = searchParams.get('to') || '';
  const toName = searchParams.get('name') || '';
  const opportunityId = searchParams.get('opportunity_id') || '';
  
  // Default to compose tab if we have a recipient email
  const activeTab = toEmail ? 'compose' : (searchParams.get('tab') || 'inbox');

  useEffect(function() { checkGmailStatus(); }, []);

  async function checkGmailStatus() {
    try {
      const res = await fetch(`${API}/api/gmail/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setGmailStatus(data);
    } catch (err) {
      console.error('Error checking Gmail status:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleTabChange(value) {
    setSearchParams({ tab: value });
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#C9A227]" />
        </div>
      </Layout>
    );
  }

  if (!gmailStatus?.connected) {
    return (
      <Layout>
        <div className="p-8 max-w-2xl mx-auto">
          <Card className="border-[#E5E7EB]">
            <CardContent className="py-12">
              <div className="text-center">
                <Mail className="w-16 h-16 text-[#9CA3AF] mx-auto mb-4" />
                <h2 className="text-xl font-medium text-[#1A1A1A] mb-2">Connect Your Gmail</h2>
                <p className="text-[#6B7280] mb-6">
                  Connect your Gmail account to access your inbox and send emails directly from the CRM.
                </p>
                <Link to="/settings/email">
                  <Button className="bg-[#1A1A1A] hover:bg-[#333]">
                    <ExternalLink className="w-4 h-4 mr-2" />Go to Email Settings
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 h-[calc(100vh-80px)]" data-testid="emails-page">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="h-full flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <TabsList className="bg-[#F3F4F6]">
              <TabsTrigger value="inbox" className="data-[state=active]:bg-white flex items-center gap-2">
                <Inbox className="w-4 h-4" />Inbox
                {unreadCount > 0 && (
                  <Badge className="bg-[#C9A227] text-white rounded-full px-2 py-0 text-xs">{unreadCount}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="compose" className="data-[state=active]:bg-white flex items-center gap-2">
                <PenSquare className="w-4 h-4" />Compose
              </TabsTrigger>
              <TabsTrigger value="sent" className="data-[state=active]:bg-white flex items-center gap-2">
                <Send className="w-4 h-4" />Sent
              </TabsTrigger>
              <TabsTrigger value="designer" className="data-[state=active]:bg-white flex items-center gap-2">
                <Mail className="w-4 h-4" />Email Designer
              </TabsTrigger>
            </TabsList>
            <p className="text-sm text-[#6B7280]">Connected: {gmailStatus.email}</p>
          </div>
          
          <TabsContent value="inbox" className="flex-1 mt-0">
            <Card className="h-full border-[#E5E7EB] overflow-hidden">
              <InboxView token={token} unreadCount={unreadCount} onUnreadChange={setUnreadCount} />
            </Card>
          </TabsContent>
          
          <TabsContent value="compose" className="flex-1 mt-0">
            <Card className="h-full border-[#E5E7EB] overflow-hidden">
              <CardHeader className="border-b border-[#E5E7EB] py-3">
                <CardTitle className="text-lg font-medium flex items-center gap-2">
                  <PenSquare className="w-5 h-5 text-[#C9A227]" />
                  New Email
                  {toName && <span className="text-sm font-normal text-[#6B7280]">to {toName}</span>}
                </CardTitle>
              </CardHeader>
              <ComposeView 
                token={token} 
                initialTo={toEmail} 
                initialName={toName} 
                opportunityId={opportunityId} 
              />
            </Card>
          </TabsContent>
          
          <TabsContent value="sent" className="flex-1 mt-0">
            <Card className="h-full border-[#E5E7EB] overflow-hidden">
              <SentView token={token} />
            </Card>
          </TabsContent>
          
          <TabsContent value="designer" className="flex-1 mt-0">
            <Card className="h-full border-[#E5E7EB]">
              <CardContent className="p-6">
                <div className="text-center py-8">
                  <PenSquare className="w-12 h-12 text-[#C9A227] mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-[#1A1A1A] mb-2">Email Designer</h3>
                  <p className="text-[#6B7280] mb-6">
                    Create beautiful branded emails with your custom template.
                  </p>
                  <Link to="/email-designer">
                    <Button className="bg-[#1A1A1A] hover:bg-[#333]">
                      <PenSquare className="w-4 h-4 mr-2" />Open Email Designer
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
